import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Empty,
  Form,
  InputNumber,
  Modal,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'

import {
  BASE_PATH,
  FULL_TRUCK_LOAD_PATH,
  PROVINCE_BASKET_SIZE_RATES_QUERY,
  REPLACE_BASKET_SIZE_RATES_MUTATION,
  formatBaht,
  formatMutationErrors,
  formatPercent,
  multiplierAsPercent
} from './graphql'

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'

const NEW_CATCH_ALL_TIER = { unlimited: true, maximum: null, percentage: null }

const toFormTiers = tiers => tiers.map(tier => ({
  unlimited: tier.maximum === null,
  maximum: tier.maximum,
  percentage: tier.percentage
}))

const toTierInputs = (tiers = []) => tiers.map(tier => ({
  maximum: tier.unlimited ? null : tier.maximum,
  percentage: tier.percentage
}))

const tierListErrors = (tiers, t) => {
  const errors = []
  const maximums = tiers.map(tier => tier.maximum)

  if (tiers.filter(tier => tier.maximum === null).length > 1) errors.push(t('basketSize.errors.oneCatchAll'))
  if (new Set(maximums.filter(maximum => maximum !== null)).size !== maximums.filter(maximum => maximum !== null).length) {
    errors.push(t('basketSize.errors.uniqueMaximum'))
  }

  return errors
}

const tierRangeLabel = (tiers, index, t) => {
  const tier = tiers[index]
  const previous = tiers.slice(0, index).filter(other => other.maximum !== null).pop()
  const from = previous ? formatBaht(previous.maximum) : '0'

  if (tier.maximum === null) {
    return previous ? t('basketSize.rangeAbove', { from }) : t('basketSize.rangeAny')
  }

  return t('basketSize.rangeUpTo', { from, to: formatBaht(tier.maximum) })
}

const FormulaExplainer = () => {
  const { t } = useTranslation('customerShippingRates')

  return (
    <Alert
      description={(
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>{t('formula.itemRateFirst')}</li>
          <li>{t('formula.tierLookup')}</li>
          <li>{t('formula.cost')}</li>
          <li>{t('formula.basePrice')}</li>
          <li>{t('formula.bangkokCap')}</li>
          <li>{t('formula.fullTruckLoadFirst')}</li>
        </ul>
      )}
      message={t('formula.title')}
      showIcon
      style={{ marginBottom: 16 }}
      type='info'
    />
  )
}

const ReadOnlyTiers = ({ tiers }) => {
  const { t } = useTranslation('customerShippingRates')

  const columns = [
    {
      title: t('basketSize.range'),
      key: 'range',
      render: (_value, _tier, index) => tierRangeLabel(tiers, index, t)
    },
    {
      title: t('basketSize.maximum'),
      dataIndex: 'maximum',
      key: 'maximum',
      align: 'right',
      render: value => (value === null ? <Tag>{t('basketSize.unlimited')}</Tag> : formatBaht(value))
    },
    {
      title: t('basketSize.multiplier'),
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: value => (
        <span>
          {value}
          <Typography.Text type='secondary'> ({multiplierAsPercent(value)})</Typography.Text>
        </span>
      )
    }
  ]

  return <Table columns={columns} dataSource={tiers} pagination={false} rowKey='id' size='small' />
}

ReadOnlyTiers.propTypes = {
  tiers: PropTypes.array.isRequired
}

const TierRow = ({ field, form, onRemove }) => {
  const { t } = useTranslation('customerShippingRates')
  const unlimited = Form.useWatch(['tiers', field.name, 'unlimited'], form)
  const percentage = Form.useWatch(['tiers', field.name, 'percentage'], form)

  return (
    <Space align='baseline' style={{ display: 'flex', marginBottom: 8 }} wrap>
      <Form.Item
        label={t('basketSize.maximum')}
        name={[field.name, 'maximum']}
        rules={unlimited ? [] : [
          { required: true, message: t('basketSize.errors.maximumRequired') },
          { type: 'number', min: 0.01, message: t('basketSize.errors.maximumPositive') }
        ]}
      >
        <InputNumber addonAfter='฿' disabled={unlimited} min={0} style={{ width: 200 }} />
      </Form.Item>
      <Form.Item name={[field.name, 'unlimited']} valuePropName='checked'>
        <Checkbox onChange={event => event.target.checked && form.setFieldValue(['tiers', field.name, 'maximum'], null)}>
          {t('basketSize.unlimited')}
        </Checkbox>
      </Form.Item>
      <Form.Item
        extra={percentage !== null && percentage !== undefined ? `= ${multiplierAsPercent(percentage)}` : null}
        label={t('basketSize.multiplier')}
        name={[field.name, 'percentage']}
        rules={[
          { required: true, message: t('basketSize.errors.percentageRequired') },
          { type: 'number', min: 0, message: t('basketSize.errors.percentageNonNegative') }
        ]}
      >
        <InputNumber min={0} placeholder='0.08' step={0.01} style={{ width: 140 }} />
      </Form.Item>
      <Button danger icon={<DeleteOutlined />} onClick={onRemove}>{t('basketSize.removeTier')}</Button>
    </Space>
  )
}

TierRow.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired
}

const TierEditor = ({ provinceId, rates, onSaved }) => {
  const { t } = useTranslation('customerShippingRates')
  const [form] = Form.useForm()
  const [submitErrors, setSubmitErrors] = useState([])
  const [replaceRates, { loading: saving }] = useMutation(REPLACE_BASKET_SIZE_RATES_MUTATION)

  const initialTiers = rates.tiers.length ? toFormTiers(rates.tiers) : [NEW_CATCH_ALL_TIER]

  const submit = async (tiers) => {
    try {
      const { data } = await replaceRates({ variables: { provinceId, tiers } })
      const payload = data.replaceBasketSizeRates
      if (payload.errors && payload.errors.length) {
        setSubmitErrors([formatMutationErrors(payload.errors)])
        return
      }
      setSubmitErrors([])
      form.setFieldsValue({ tiers: toFormTiers(payload.rates.tiers) })
      message.success(t('saved'))
      onSaved()
    } catch (mutationError) {
      setSubmitErrors([mutationError.message])
    }
  }

  const handleFinish = (values) => {
    const tiers = toTierInputs(values.tiers)
    const errors = tierListErrors(tiers, t)
    setSubmitErrors(errors)
    if (errors.length) return

    if (!tiers.length) {
      Modal.confirm({
        content: t('basketSize.removeAllConfirm', { province: rates.province.name }),
        okButtonProps: { danger: true },
        onOk: () => submit(tiers)
      })
      return
    }

    submit(tiers)
  }

  return (
    <Form form={form} initialValues={{ tiers: initialTiers }} layout='vertical' onFinish={handleFinish}>
      <Form.List name='tiers'>
        {(fields, { add, remove }) => (
          <React.Fragment>
            {!fields.length && <Empty description={t('basketSize.noTiers')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            {fields.map(field => (
              <TierRow field={field} form={form} key={field.key} onRemove={() => remove(field.name)} />
            ))}
            <Button icon={<PlusOutlined />} onClick={() => add({ unlimited: false, maximum: null, percentage: null })} type='dashed'>
              {t('basketSize.addTier')}
            </Button>
          </React.Fragment>
        )}
      </Form.List>

      {submitErrors.length > 0 && (
        <Alert
          message={submitErrors.join(', ')}
          showIcon
          style={{ marginTop: 16 }}
          type='error'
        />
      )}

      <Space style={{ marginTop: 16 }}>
        <Button htmlType='submit' loading={saving} type='primary'>{t('save')}</Button>
        <Button onClick={() => { form.resetFields(); setSubmitErrors([]) }}>{t('reset')}</Button>
      </Space>
    </Form>
  )
}

TierEditor.propTypes = {
  onSaved: PropTypes.func.isRequired,
  provinceId: PropTypes.string.isRequired,
  rates: PropTypes.object.isRequired
}

const BasketSizeRatesDetail = () => {
  const { t } = useTranslation('customerShippingRates')
  const { provinceId } = useParams()

  const { data, error, loading, refetch } = useQuery(PROVINCE_BASKET_SIZE_RATES_QUERY, {
    variables: { provinceId },
    fetchPolicy: 'network-only'
  })

  const rates = data && data.basketSizeRates[0]

  useEffect(() => {
    document.title = 'Customer Shipping Rates | OSH CRM'
  }, [])

  const renderBody = () => {
    if (loading && !data) return <Spin />
    if (error) return <Alert message={error.message} showIcon type='error' />
    if (!rates) return <Result status='404' title={t('basketSize.provinceNotFound')} />

    return (
      <React.Fragment>
        <Descriptions bordered column={1} size='small' style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('province')}>{rates.province.name}</Descriptions.Item>
          <Descriptions.Item label={t('basketSize.fullTruckLoadPercentage')}>
            <Space>
              {formatPercent(rates.fullTruckLoadPercentage)}
              <Link to={`${FULL_TRUCK_LOAD_PATH}?province=${rates.province.id}`}>{t('basketSize.editFullTruckLoad')}</Link>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('updatedAt')}>
            {rates.updatedAt ? dayjs(rates.updatedAt).format(DATE_TIME_FORMAT) : '-'}
          </Descriptions.Item>
        </Descriptions>

        <FormulaExplainer />

        {rates.tiers.length > 0 && !rates.hasCatchAll && (
          <Alert message={t('basketSize.missingCatchAllWarning')} showIcon style={{ marginBottom: 16 }} type='warning' />
        )}

        <Card size='small' title={t('basketSize.tiers')}>
          {rates.canEdit
            ? <TierEditor key={rates.updatedAt || 'new'} onSaved={() => refetch()} provinceId={provinceId} rates={rates} />
            : (rates.tiers.length ? <ReadOnlyTiers tiers={rates.tiers} /> : <Empty description={t('basketSize.noTiers')} />)}
        </Card>
      </React.Fragment>
    )
  }

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: BASE_PATH, name: t('title') },
        { name: rates ? rates.province.name : provinceId }
      ]}>
        <ContentLayout>
          {renderBody()}
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

export default BasketSizeRatesDetail
